import React, { useState } from 'react';
import { login } from '../api';
import logo from '../logo.png';

function useIsMobile() {
  const [isMobile] = useState(window.innerWidth < 768);
  return isMobile;
}

export default function Login({ onLogin }) {
  const [clientCode, setClientCode] = useState('');
  const [password,   setPassword]   = useState('');
  const [error,      setError]      = useState('');
  const [loading,    setLoading]    = useState(false);
  const isMobile = useIsMobile();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!clientCode || !password) {
      setError('Please enter client code and password');
      return;
    }
    setLoading(true);
    login(clientCode, password)
      .then(res => {
        onLogin(res.data);
      })
      .catch(err => {
        const msg = err.response?.data?.detail || 'Login failed. Please try again.';
        setError(msg);
        setLoading(false);
      });
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px',
    border: '1px solid #e0d8d5', borderRadius: 8,
    fontSize: 14, outline: 'none', marginBottom: 16,
    color: '#333', background: '#fdfcfc', boxSizing: 'border-box',
  };

  const labelStyle = {
    fontSize: 12, color: '#444', fontWeight: 600,
    marginBottom: 6, display: 'block',
  };

  // Single form — never unmounts regardless of layout switch
  const renderForm = () => (
    <div style={{
      background: '#fff', borderRadius: 16,
      padding: isMobile ? '28px 24px' : '40px',
      width: '100%', maxWidth: 400,
      boxShadow: isMobile ? '0 10px 40px rgba(0,0,0,0.2)' : '0 10px 40px rgba(66,0,11,0.06)',
      border: isMobile ? 'none' : '1px solid rgba(66,0,11,0.04)',
      position: 'relative', zIndex: 2,
    }}>
      <div style={{ fontSize: isMobile ? 20 : 22, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>
        Client Login
      </div>
      <div style={{ fontSize: 13, color: '#777', marginBottom: 20 }}>PCSPL Pledge+ Portal</div>

      {error && (
        <div style={{
          background: '#fdf0f0', border: '1px solid #f5c6c6',
          borderRadius: 8, padding: '12px 16px',
          fontSize: 13, color: '#c0392b',
          marginBottom: 16, lineHeight: 1.5,
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} autoComplete="off">
        <label style={labelStyle}>Client Code</label>
        <input
          style={inputStyle}
          type="text" inputMode="numeric" pattern="[0-9]*"
          placeholder="e.g. 10001" value={clientCode}
          onChange={e => setClientCode(e.target.value)}
          onFocus={e => e.target.style.borderColor = '#42000B'}
          onBlur={e => e.target.style.borderColor = '#e0d8d5'}
        />
        <label style={labelStyle}>Password</label>
        <input
          style={inputStyle}
          type="password" placeholder="Enter your password" value={password}
          onChange={e => setPassword(e.target.value)}
          onFocus={e => e.target.style.borderColor = '#42000B'}
          onBlur={e => e.target.style.borderColor = '#e0d8d5'}
        />
        <button
          type="submit" disabled={loading}
          style={{
            width: '100%', padding: 13,
            background: '#42000B', color: '#fff',
            border: 'none', borderRadius: 8,
            fontSize: 14, fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            marginTop: 4, opacity: loading ? 0.8 : 1,
          }}
        >
          {loading ? 'Logging in...' : 'Login'}
        </button>
      </form>

      <div style={{ textAlign: 'center', marginTop: 14, fontSize: 12, color: '#999' }}>
        Forgot password? Contact your portfolio manager.
      </div>
    </div>
  );

  // Single unified layout — grid switches between 1 and 2 columns
  return (
    <div style={{
      minHeight: '100vh',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1.1fr 0.9fr',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      background: isMobile ? '#42000B' : '#fcfbfa',
    }}>

      {/* Marketing pane — left on desktop, bottom on mobile */}
      <div style={{
        background: '#42000B',
        padding: isMobile ? '24px 24px 40px' : '60px 80px',
        display: 'flex', flexDirection: 'column',
        justifyContent: 'space-between', color: '#fff',
        order: isMobile ? 2 : 1,
      }}>
        {!isMobile && <img src={logo} alt="Pashupati Capital" style={{ height: 52, alignSelf: 'flex-start' }} />}

        <div style={{ maxWidth: 520, margin: isMobile ? '0 0 24px 0' : '40px 0' }}>
          <div style={{ fontSize: isMobile ? 22 : 36, fontWeight: 700, marginBottom: 12, letterSpacing: '-0.02em' }}>
            PCSPL Pledge+
          </div>
          <div style={{ fontSize: isMobile ? 15 : 18, color: 'rgba(255,255,255,0.9)', marginBottom: 16, lineHeight: 1.4 }}>
            Unlock <span style={{ color: '#ff4d6d', fontWeight: 600 }}>extra returns</span> on your share collateral.
          </div>
          <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, marginBottom: 24 }}>
            For HNI investors (₹1 Cr+), Pledge+ turns your shareholdings into a high-yield asset — without selling a single share.
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {[
              ['Custom collateral lending', 'at competitive rates'],
              ['Flexible tenure', 'from 30 days to 1 year'],
              ['Transparent P&L dashboard', 'with real-time interest accrual'],
            ].map(([bold, rest]) => (
              <li key={bold} style={{ fontSize: 14, marginBottom: 12, display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <span style={{ color: '#ff4d6d', fontSize: 16, fontWeight: 'bold', flexShrink: 0 }}>•</span>
                <div><strong style={{ color: '#ff4d6d' }}>{bold}</strong> {rest}</div>
              </li>
            ))}
          </ul>
        </div>

        <div style={{ display: 'flex', gap: 40, borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: 24, fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontWeight: 600, color: '#fff' }}>Contact us</span>
            <span>(91-22) 40345500</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontWeight: 600, color: '#fff' }}>Email</span>
            <span>info@pashupaticapital.com</span>
          </div>
        </div>
      </div>

      {/* Form pane — right on desktop, top on mobile */}
      <div style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: isMobile ? '40px 24px 10px' : '40px',
        background: isMobile ? '#42000B' : '#fcfbfa',
        position: 'relative',
        order: isMobile ? 1 : 2,
      }}>
        {isMobile && (
          <img src={logo} alt="Pashupati Capital" style={{ height: 48, marginBottom: 28 }} />
        )}

        {!isMobile && (
          <svg style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0, pointerEvents: 'none', opacity: 0.12 }}
            viewBox="0 0 800 600" fill="none">
            <path d="M-50,450 L120,380 L280,510 L450,220 L620,340 L850,110" stroke="#42000B" strokeWidth="2" strokeLinecap="round" />
          </svg>
        )}

        {renderForm()}

        {!isMobile && (
          <div style={{ position: 'absolute', bottom: 24, fontSize: 12, color: '#999', zIndex: 2 }}>
            Pashupati Capital Services Pvt. Ltd.
          </div>
        )}
      </div>
    </div>
  );
}
